import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";

import { createWebRTCDebugTap } from "lib/webrtc/debugTap";
import {
    ChunkBuffer,
    clearStoredChunks,
    GENERAL_LOG,
    isDebugLogStoreAvailable,
    readEntriesInRange,
    type TimeRange,
    writeChunk,
    type DebugLogEntry,
} from "lib/webrtc/debugLogStore";
import {
    readInboundVideo,
    StreamStatsTracker,
    type StreamStatsSnapshot,
} from "lib/webrtc/debugStats";
import { useWebRTC } from "providers/WebRTCProvider";

const ENABLED_STORAGE_KEY = "webrtc-debug-enabled";
const CURRENT_ONLY_STORAGE_KEY = "webrtc-debug-current-connection-only";
/** Newest entries kept in memory per stream for the popup. */
const MEMORY_ENTRIES_PER_STREAM = 500;
const UI_REFRESH_MS = 500;
const STATS_POLL_MS = 1000;
const EMPTY_STREAMS: string[] = [];

type HistoryState = {
    entries: DebugLogEntry[];
    /** True when the range held more than the read limit; the oldest entries were dropped. */
    truncated: boolean;
    loading: boolean;
    error: string | null;
};

const EMPTY_HISTORY: HistoryState = { entries: [], truncated: false, loading: false, error: null };

type WebRTCDebugContextValue = {
    /** False when the build uses the Janus transport, which has no debug tap. */
    supported: boolean;
    enabled: boolean;
    setEnabled: (enabled: boolean) => void;
    streams: string[];
    selectedStream: string | null;
    setSelectedStream: (stream: string) => void;
    /** Entries of the selected stream, oldest first. */
    entries: DebugLogEntry[];
    stats: Map<string, StreamStatsSnapshot>;
    currentConnectionOnly: boolean;
    setCurrentConnectionOnly: (value: boolean) => void;
    storageError: string | null;
    /** Stored entries from the last `loadHistory` call, all streams, oldest first. */
    history: HistoryState;
    /** Serial of the ARC the live view follows, or null. */
    arcSerial: string | null;
    /** Flushes pending writes, then reads stored entries inside the range. */
    loadHistory: (range: TimeRange) => Promise<void>;
    /** Clears the in-memory log and every stored chunk. */
    clearLogs: () => Promise<void>;
};

const WebRTCDebugContext = createContext<WebRTCDebugContextValue | null>(null);
WebRTCDebugContext.displayName = "WebRTCDebug";

export const useWebRTCDebug = (): WebRTCDebugContextValue => {
    const ctx = useContext(WebRTCDebugContext);
    if (!ctx) {
        throw new Error("useWebRTCDebug must be used within WebRTCDebugProvider");
    }
    return ctx;
};

const readStoredFlag = (key: string, fallback: boolean): boolean => {
    if (typeof window === "undefined") return fallback;
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : raw === "true";
};

type Props = {
    children: ReactNode;
};

/**
 * Collects the WebRTC developer log. It must be nested under WebRTCProvider:
 * that provider owns the capture tap and the viewer peer connections, this one
 * owns the enable switch, the in-memory view, and IndexedDB storage.
 */
const WebRTCDebugProvider = ({ children }: Props) => {
    const webrtc = useWebRTC() as {
        debugTap?: ReturnType<typeof createWebRTCDebugTap>;
        availableStreams?: string[];
        arcSerial?: string | null;
    } | null;
    const tap = webrtc?.debugTap ?? null;
    const availableStreams = webrtc?.availableStreams ?? EMPTY_STREAMS;
    const arcSerial = webrtc?.arcSerial ?? null;

    const [enabled, setEnabledState] = useState(() => readStoredFlag(ENABLED_STORAGE_KEY, false));
    const [currentConnectionOnly, setCurrentConnectionOnlyState] = useState(() =>
        readStoredFlag(CURRENT_ONLY_STORAGE_KEY, true),
    );
    const [selectedStream, setSelectedStream] = useState<string | null>(null);
    const [entriesByStream, setEntriesByStream] = useState<Map<string, DebugLogEntry[]>>(() => new Map());
    const [stats, setStats] = useState<Map<string, StreamStatsSnapshot>>(() => new Map());
    const [storageError, setStorageError] = useState<string | null>(null);

    const entriesRef = useRef<Map<string, DebugLogEntry[]>>(new Map());
    const trackersRef = useRef<Map<string, StreamStatsTracker>>(new Map());
    const dirtyRef = useRef(false);
    const arcSerialRef = useRef<string | null>(arcSerial);
    const writeQueueRef = useRef<Promise<void>>(Promise.resolve());
    const drainRef = useRef<(() => void) | null>(null);
    const historyRequestRef = useRef(0);
    const [history, setHistory] = useState<HistoryState>(EMPTY_HISTORY);

    const setEnabled = useCallback((next: boolean) => {
        setEnabledState(next);
        window.localStorage.setItem(ENABLED_STORAGE_KEY, String(next));
    }, []);

    const setCurrentConnectionOnly = useCallback((next: boolean) => {
        setCurrentConnectionOnlyState(next);
        window.localStorage.setItem(CURRENT_ONLY_STORAGE_KEY, String(next));
    }, []);

    // The live view follows the selected ARC; other ARCs' entries stay in History.
    useEffect(() => {
        arcSerialRef.current = arcSerial;
        entriesRef.current = new Map();
        trackersRef.current = new Map();
        dirtyRef.current = true;
    }, [arcSerial]);

    const publish = useCallback(() => {
        if (!dirtyRef.current) return;
        dirtyRef.current = false;
        setEntriesByStream(new Map([...entriesRef.current].map(([key, list]) => [key, [...list]])));
        setStats(new Map([...trackersRef.current].map(([key, tracker]) => [key, tracker.snapshot()])));
    }, []);

    useEffect(() => {
        if (!tap || !enabled) return undefined;

        const buffer = new ChunkBuffer(new Date().toISOString());
        // Writes are queued so a history read can wait for everything flushed before it.
        const persist = (chunk: ReturnType<ChunkBuffer["drain"]>) => {
            if (!chunk) return;
            writeQueueRef.current = writeQueueRef.current
                .then(() => writeChunk(chunk))
                .then(() => setStorageError(null))
                .catch((error: unknown) => setStorageError(error instanceof Error ? error.message : String(error)));
        };
        drainRef.current = () => persist(buffer.drain());
        if (!isDebugLogStoreAvailable()) setStorageError("IndexedDB is not available in this browser.");

        const trackerFor = (cameraKey: string) => {
            let tracker = trackersRef.current.get(cameraKey);
            if (!tracker) {
                tracker = new StreamStatsTracker();
                trackersRef.current.set(cameraKey, tracker);
            }
            return tracker;
        };

        const unsubscribe = tap.subscribe((event: DebugLogEntry) => {
            const { connectionId } = event;
            const cameraKey = event.cameraKey ?? GENERAL_LOG;
            if (event.kind === "connection" && connectionId && event.cameraKey && (event.arcSerial ?? null) === arcSerialRef.current) {
                const tracker = trackerFor(cameraKey);
                if (event.phase === "open") tracker.open(connectionId);
                else tracker.close(connectionId);
            }
            persist(buffer.push(event));
            // Stored for every ARC, but the live view holds only the selected one.
            if ((event.arcSerial ?? null) !== arcSerialRef.current) return;
            const list = entriesRef.current.get(cameraKey) ?? [];
            list.push(event);
            if (list.length > MEMORY_ENTRIES_PER_STREAM) list.splice(0, list.length - MEMORY_ENTRIES_PER_STREAM);
            entriesRef.current.set(cameraKey, list);
            dirtyRef.current = true;
        });
        tap.setEnabled(true);

        let polling = false;
        const pollStats = async () => {
            if (polling) return;
            polling = true;
            try {
                await Promise.all(tap.getPeers().map(async (peer) => {
                    try {
                        const sample = readInboundVideo(await peer.rtc.getStats());
                        if (!sample) return;
                        trackerFor(peer.cameraKey).update(peer.connectionId, sample);
                        dirtyRef.current = true;
                    } catch {
                        // The peer connection closed between listing and sampling.
                    }
                }));
            } finally {
                polling = false;
            }
        };

        const statsTimer = window.setInterval(pollStats, STATS_POLL_MS);
        const refreshTimer = window.setInterval(publish, UI_REFRESH_MS);
        const flushWhenHidden = () => {
            if (document.visibilityState === "hidden") persist(buffer.drain());
        };
        document.addEventListener("visibilitychange", flushWhenHidden);

        return () => {
            document.removeEventListener("visibilitychange", flushWhenHidden);
            window.clearInterval(statsTimer);
            window.clearInterval(refreshTimer);
            tap.setEnabled(false);
            unsubscribe();
            persist(buffer.drain());
            drainRef.current = null;
        };
    }, [tap, enabled, publish]);

    const loadHistory = useCallback(async (range: TimeRange) => {
        historyRequestRef.current += 1;
        const request = historyRequestRef.current;
        setHistory((previous) => ({ ...previous, loading: true, error: null }));
        try {
            drainRef.current?.();
            await writeQueueRef.current;
            const result = await readEntriesInRange(range);
            if (request === historyRequestRef.current) setHistory({ ...result, loading: false, error: null });
        } catch (error) {
            if (request !== historyRequestRef.current) return;
            setHistory({ ...EMPTY_HISTORY, error: error instanceof Error ? error.message : String(error) });
        }
    }, []);

    const clearLogs = useCallback(async () => {
        entriesRef.current = new Map();
        trackersRef.current = new Map();
        dirtyRef.current = false;
        setEntriesByStream(new Map());
        setStats(new Map());
        historyRequestRef.current += 1;
        setHistory(EMPTY_HISTORY);
        try {
            await clearStoredChunks();
            setStorageError(null);
        } catch (error) {
            setStorageError(error instanceof Error ? error.message : String(error));
        }
    }, []);

    const streams = useMemo(() => {
        // General is always listed so the log stays readable with no streams.
        const names = new Set([GENERAL_LOG, ...availableStreams, ...entriesByStream.keys()]);
        return [...names];
    }, [availableStreams, entriesByStream]);

    const activeStream = selectedStream && streams.includes(selectedStream) ? selectedStream : streams[0] ?? null;
    const entries = useMemo(
        () => (activeStream ? entriesByStream.get(activeStream) ?? [] : []),
        [activeStream, entriesByStream],
    );

    const value = useMemo<WebRTCDebugContextValue>(
        () => ({
            supported: tap !== null,
            enabled: enabled && tap !== null,
            setEnabled,
            streams,
            selectedStream: activeStream,
            setSelectedStream,
            entries,
            stats,
            currentConnectionOnly,
            setCurrentConnectionOnly,
            storageError,
            arcSerial,
            history,
            loadHistory,
            clearLogs,
        }),
        [tap, enabled, setEnabled, streams, activeStream, entries, stats, currentConnectionOnly, setCurrentConnectionOnly, storageError, arcSerial, history, loadHistory, clearLogs],
    );

    return <WebRTCDebugContext.Provider value={value}>{children}</WebRTCDebugContext.Provider>;
};

export default WebRTCDebugProvider;
